<template>
  <VaDropdown :offset="[13, 0]" class="notification-dropdown" stick-to-edges :close-on-content-click="false">
    <template #anchor>
      <VaButton preset="secondary" color="textPrimary">
        <VaBadge overlap>
          <template #text>{{ totalDrivers }}+</template>
          <VaIconFree class="notification-dropdown__icon" />
        </VaBadge>
      </VaButton>
    </template>
    <VaDropdownContent class="h-full sm:max-w-[420px] sm:h-auto">
      <!-- Header with Export Button -->
      <div class="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700">
        <div class="flex-1">
          <!-- Tab Navigation -->
          <div class="flex">
            <button
              v-for="tab in tabs"
              :key="tab.status"
              :class="[
                'px-4 py-2 text-sm font-medium',
                activeTab === tab.status
                  ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300',
              ]"
              @click="activeTab = tab.status"
            >
              {{ tab.label }} ({{ tab.count }})
            </button>
          </div>
        </div>
        <VaButton preset="secondary" size="small" class="ml-2" @click.stop="exportToCSV">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-4 h-4 mr-1"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
            />
          </svg>
          Export
        </VaButton>
      </div>

      <!-- Tab Content -->
      <div class="overflow-y-auto max-h-[800px]">
        <div v-for="tab in tabs" :key="tab.status">
          <div v-if="activeTab === tab.status" class="p-2">
            <ul role="list" class="divide-y divide-gray-200 dark:divide-gray-700">
              <li
                v-for="driver in getDriversByStatus(tab.status)"
                :key="driver.driverInfo.driverUuid"
                class="py-3 sm:py-4 px-4 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                @click="navigateToDriver(driver.driverInfo.driverUuid)"
              >
                <div class="flex items-center space-x-3 rtl:space-x-reverse">
                  <div class="flex-shrink-0">
                    <div
                      :class="['w-8 h-8 rounded-full flex items-center justify-center text-white', driver.statusClass]"
                    >
                      {{ getAvatarText(driver) }}
                    </div>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold text-gray-900 truncate dark:text-white">
                      {{ driver.driverInfo.firstName }} {{ driver.driverInfo.lastName }}
                    </p>
                    <p class="text-sm text-gray-500 truncate dark:text-gray-400">
                      {{ driver.vehicleInfo.licensePlate || 'No vehicle' }}
                    </p>
                  </div>
                  <span
                    :class="driver.statusClass"
                    class="inline-flex items-center text-xs font-medium px-2.5 py-0.5 rounded-full"
                  >
                    <span :class="driver.statusIndicator" class="w-2 h-2 me-1 rounded-full"></span>
                    {{ driver.statusText }}
                  </span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </VaDropdownContent>
  </VaDropdown>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vuestic-ui'
import { downloadAsCSV } from '@/services/toCSV'
import { getAuthHeaders, valtFetch } from '@/utils/api'
import VaIconFree from '../../../icons/VaIconFree.vue'

const router = useRouter()
const { init: notify } = useToast()

interface DriverStatusOverview {
  driverInfo: {
    driverUuid: string
    firstName: string
    lastName: string
    phone: string
    email: string
  }
  vehicleInfo: {
    licensePlate?: string
  }
  statusEntries:
    | {
        status: string
        timestamp: string
      }[]
    | null
  onboardingStatus: string
}

const driverStatusOverviews = ref<DriverStatusOverview[]>([])
let pollingInterval: number | null = null
const activeTab = ref('DRIVER_STATUS_ONLINE') // Default to Online tab

// Function to navigate to driver profile
const navigateToDriver = (driverUuid: string) => {
  const route = router.resolve({ name: 'driversingle', params: { carNumber: driverUuid } })
  window.location.href = route.href
}

// Function to get the initials for the avatar (First and Last Name)
const getAvatarText = (driver: DriverStatusOverview) => {
  const firstInitial = driver.driverInfo.firstName.charAt(0).toUpperCase()
  const lastInitial = driver.driverInfo.lastName.charAt(0).toUpperCase()
  return `${firstInitial}${lastInitial}`
}

// Count of all drivers (removed the active filter)
const totalDrivers = computed(() => {
  return driverStatusOverviews.value.length
})

// Enhanced drivers with status information (now includes all drivers)
const enhancedDrivers = computed(() => {
  return driverStatusOverviews.value.map((driver) => {
    const status = getDriverStatus(driver)
    return {
      ...driver,
      statusText: getStatusText(status),
      statusClass: getStatusClass(status),
      statusIndicator: getStatusIndicator(status),
      statusValue: status,
    }
  })
})

// Tabs configuration
const tabs = computed(() => {
  const statusCounts = {
    DRIVER_STATUS_ONLINE: 0,
    DRIVER_STATUS_ONTRIP: 0,
    DRIVER_STATUS_OFFLINE: 0,
    noStatus: 0,
  }

  enhancedDrivers.value.forEach((driver) => {
    if (driver.statusValue === 'DRIVER_STATUS_ONLINE') {
      statusCounts['DRIVER_STATUS_ONLINE']++
    } else if (driver.statusValue === 'DRIVER_STATUS_ONTRIP') {
      statusCounts['DRIVER_STATUS_ONTRIP']++
    } else if (driver.statusValue === 'DRIVER_STATUS_OFFLINE') {
      statusCounts['DRIVER_STATUS_OFFLINE']++
    } else {
      statusCounts['noStatus']++
    }
  })

  return [
    { status: 'DRIVER_STATUS_ONLINE', label: 'Online', count: statusCounts['DRIVER_STATUS_ONLINE'] },
    { status: 'DRIVER_STATUS_ONTRIP', label: 'On Trip', count: statusCounts['DRIVER_STATUS_ONTRIP'] },
    { status: 'DRIVER_STATUS_OFFLINE', label: 'Offline', count: statusCounts['DRIVER_STATUS_OFFLINE'] },
    { status: 'noStatus', label: 'No Status', count: statusCounts['noStatus'] },
  ].filter((tab) => tab.count > 0) // Only show tabs with drivers
})

// Get drivers for the current active tab
const getDriversByStatus = (status: string) => {
  return enhancedDrivers.value.filter((driver) => {
    if (status === 'noStatus') {
      return (
        !driver.statusValue ||
        (driver.statusValue !== 'DRIVER_STATUS_ONLINE' &&
          driver.statusValue !== 'DRIVER_STATUS_ONTRIP' &&
          driver.statusValue !== 'DRIVER_STATUS_OFFLINE')
      )
    }
    return driver.statusValue === status
  })
}

// Helper function to get driver status
const getDriverStatus = (driver: DriverStatusOverview): string | null => {
  if (driver.statusEntries && driver.statusEntries.length > 0) {
    return driver.statusEntries[0].status
  }
  return null
}

// Helper function to get status text
const getStatusText = (status: string | null): string => {
  switch (status) {
    case 'DRIVER_STATUS_ONLINE':
      return 'Online'
    case 'DRIVER_STATUS_ONTRIP':
      return 'On Trip'
    case 'DRIVER_STATUS_OFFLINE':
      return 'Offline'
    default:
      return 'No Status'
  }
}

// Helper function to get status class
const getStatusClass = (status: string | null): string => {
  switch (status) {
    case 'DRIVER_STATUS_ONLINE':
      return 'bg-green-100 text-green-800'
    case 'DRIVER_STATUS_ONTRIP':
      return 'bg-blue-100 text-blue-800'
    case 'DRIVER_STATUS_OFFLINE':
      return 'bg-red-100 text-red-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

// Helper function to get status indicator
const getStatusIndicator = (status: string | null): string => {
  switch (status) {
    case 'DRIVER_STATUS_ONLINE':
      return 'bg-green-500'
    case 'DRIVER_STATUS_ONTRIP':
      return 'bg-blue-500'
    case 'DRIVER_STATUS_OFFLINE':
      return 'bg-red-500'
    default:
      return 'bg-gray-500'
  }
}

const fetchDriverStatus = async () => {
  try {
    // Get region from localStorage
    const region = localStorage.getItem('region')
    const regionParam = region ? `?region=${region}` : ''

    const response = await valtFetch(`http://13.247.83.135:953/api/driver/statuses${regionParam}`, {
      headers: getAuthHeaders(),
    })
    if (!response.ok) {
      throw new Error('Failed to fetch driver data')
    }
    const data = await response.json()
    driverStatusOverviews.value = data.driverActions || []
    // console.log(driverStatusOverviews.value)
  } catch (error) {
    console.error('Error fetching drivers:', error)
  }
}

// Start polling
const startPolling = () => {
  fetchDriverStatus() // Initial fetch
  pollingInterval = window.setInterval(fetchDriverStatus, 5 * 60 * 1000) // 5 minutes
}

// Fetch driver data on component mount
onMounted(() => {
  startPolling()
})

// Clean up interval on component unmount
onBeforeUnmount(() => {
  if (pollingInterval) {
    clearInterval(pollingInterval)
  }
})

// Export driver status data to CSV
const exportToCSV = () => {
  try {
    if (!driverStatusOverviews.value.length) {
      notify({ message: 'No data to export', color: 'warning' })
      return
    }

    // Format data for export
    const exportData = driverStatusOverviews.value.map((driver) => {
      const status = getDriverStatus(driver)
      const statusText = getStatusText(status)
      const latestStatusEntry = driver.statusEntries && driver.statusEntries.length > 0 ? driver.statusEntries[0] : null

      return {
        'Driver UUID': driver.driverInfo.driverUuid || '',
        'First Name': driver.driverInfo.firstName || '',
        'Last Name': driver.driverInfo.lastName || '',
        Phone: driver.driverInfo.phone || '',
        Email: driver.driverInfo.email || '',
        'License Plate': driver.vehicleInfo?.licensePlate || 'No vehicle',
        Status: statusText,
        'Status Code': status || 'No Status',
        'Status Timestamp': latestStatusEntry?.timestamp || '',
        'Onboarding Status': driver.onboardingStatus || '',
      }
    })

    // Generate filename with date
    const date = new Date().toISOString().slice(0, 10)
    const region = localStorage.getItem('region') || 'all'
    const filename = `driver_statuses_${region}_${date}.csv`

    // Export using the utility function
    downloadAsCSV(exportData, filename)

    notify({ message: 'Export successful', color: 'success' })
  } catch (error) {
    console.error('Export failed:', error)
    notify({ message: 'Export failed', color: 'danger' })
  }
}
</script>

<style lang="scss" scoped>
.notification-dropdown {
  cursor: pointer;

  .notification-dropdown__icon {
    position: relative;
    display: flex;
    align-items: center;
  }

  .va-dropdown__anchor {
    display: inline-block;
  }

  .avatar {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #cccccc;
    color: white;
    border-radius: 50%;
    width: 2rem;
    height: 2rem;
    font-size: 1rem;
  }
}
</style>
